import {Injectable, inject} from '@angular/core';
import {Router, NavigationEnd} from '@angular/router';
import {filter} from 'rxjs/operators';
import {WindowRef} from '../providers/WindowRef';
import {ConfigService} from "./config.service";
import {EcommerceEvents} from "../interfaces/ecommerce-events";

interface CustomWindow extends Window {
  dataLayer: any[];
}

@Injectable({
  providedIn: 'root'
})
export class GtmService {
  private router = inject(Router);
  private windowRef = inject(WindowRef);
  private configService = inject(ConfigService);
  private currentPage: string = '';
  private lastTrackedEvent: string = '';
  private lastEventTime: number = 0;
  private customTrackingParams: any;
  private recursionDepth = 0;
  private maxRecursionDepth = 10;
  private eventHistory: string[] = [];
  private gtmDisabled: boolean = true;

  constructor() {
    if (this.gtmDisabled) {
      console.log('GTM disabled');
      return;
    }

    (window as any).dataLayer = (window as any).dataLayer || [];

    const win = this.windowRef.nativeWindow as CustomWindow;
    win.dataLayer = win.dataLayer || [];

    if (!(win as any).gtmRouterTrackingInitialized) {
      this.setupRouterTracking();
      (win as any).gtmRouterTrackingInitialized = true;
    }

    if (!(win as any).gtmAutoTrackingInitialized) {
      this.setupAutoTracking();
      (win as any).gtmAutoTrackingInitialized = true;
    }
  }

  initGtm(gtmId: string): void {

    if (this.gtmDisabled) {
      console.log('GTM disabled');
      return;
    }

    const win = this.windowRef.nativeWindow as CustomWindow;

    if ((win as any).gtmInitialized) {
      return;
    }

    const script = document.createElement('script');
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `;
    document.head.appendChild(script);
    (win as any).gtmInitialized = true;

    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.appendChild(noscript);
  }

  setCurrentPage(pageName: string): void {
    this.currentPage = pageName;
  }

  getCurrentPage(): string {
    return this.currentPage;
  }

  private isProcessing = false;

  private sanitizeObject(obj: any): any {
    try {
      return JSON.parse(JSON.stringify(obj));
    } catch (error) {
      console.warn('Object has circular references, returning simplified version', error);
      return {sanitized: true, error: 'circular_reference'};
    }
  }

  pushEvent(event: any): void {

    if (this.gtmDisabled) {
      console.log('GTM disabled');
      return;
    }

    const win = this.windowRef.nativeWindow as CustomWindow;

    if (!win.dataLayer) {
      console.error('dataLayer not initialized');
      return;
    }

    if (this.recursionDepth >= this.maxRecursionDepth) {
      console.error('Maximum recursion depth reached, aborting event push');
      return;
    }

    // Traccia eventi per debug
    const eventSignature = JSON.stringify(event).substring(0, 100);
    this.eventHistory.push(eventSignature);
    if (this.eventHistory.length > 20) {
      this.eventHistory.shift();
    }

    this.recursionDepth++;

    try {
      if (!event.event) {
        event.event = 'custom_event';
      }

      const sanitizedEvent = this.sanitizeObject(event);
      const ecommerceEvents = [
        'begin_checkout', 'view_cart', 'add_shipping_info',
        'add_payment_info', 'purchase', 'view_item_list',
        'select_item', 'view_item'
      ];

      let eventToPush;
      if (sanitizedEvent.event === 'updatevirtualpath') {
        eventToPush = sanitizedEvent;
      } else if (sanitizedEvent.event === 'uaevent') {
        eventToPush = sanitizedEvent;
      } else if (ecommerceEvents.includes(sanitizedEvent.event)) {
        eventToPush = sanitizedEvent;
      } else {
        eventToPush = {
          event: sanitizedEvent.event || 'unknown',
          ...sanitizedEvent
        };
      }

      // Push asincrono con setTimeout
      setTimeout(() => {
        try {
          console.log('Pushing event to dataLayer:', eventToPush);
          win.dataLayer.push(eventToPush);
        } catch (pushError) {
          console.error('Error during dataLayer push:', pushError);
        }
      }, 0);

    } catch (error) {
      console.error('Error preparing event for dataLayer:', error);
    } finally {
      setTimeout(() => {
        this.recursionDepth--;
      }, 100);
    }
  }

  private eventQueue: any[] = [];
  private batchTimeout: any;

  queueEvent(event: any): void {
    this.eventQueue.push(event);

    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
    }

    this.batchTimeout = setTimeout(() => {
      this.flushEventQueue();
    }, 50);
  }

  private flushEventQueue(): void {
    if (this.eventQueue.length === 0) return;

    const eventsToProcess = [...this.eventQueue];
    this.eventQueue = [];

    eventsToProcess.forEach(event => {
      this.pushEvent(event);
    });
  }

  trackEventSafely(eventName: string, eventParams?: any): void {
    try {
      let category = '';
      let action = '';
      let label = '';

      if (eventName && (eventName.includes('::') || eventName.includes(':'))) {
        let normalizedName = eventName;
        normalizedName = normalizedName.replace(/:((?!:))/g, '::$1');

        const parts = normalizedName.split('::');

        if (parts.length >= 1) category = parts[0];
        if (parts.length >= 2) action = parts[1];
        if (parts.length >= 3) {
          label = parts.slice(2).join('::');
          if (label.startsWith(':')) {
            label = label.substring(1);
          }
        }
      }

      const gtmEvent = {
        event: 'uaevent',
        eventCategory: category,
        eventAction: action,
        eventLabel: label,
        ...eventParams
      };

      this.queueEvent(gtmEvent);
    } catch (error) {
      console.error('Error tracking event safely:', error);
    }
  }

  checkGtmHealth(): boolean {
    const win = this.windowRef.nativeWindow as CustomWindow;

    const hasDataLayer = !!win.dataLayer;
    const hasGtmStart = win.dataLayer ? win.dataLayer.some(e => e.event === 'gtm.start') : false;
    const hasGtmJs = win.dataLayer ? win.dataLayer.some(e => e.event === 'gtm.js') : false;

    const isHealthy = hasDataLayer && hasGtmStart && hasGtmJs;

    if (!isHealthy) {
      console.warn('GTM Health Check Failed:', {
        hasDataLayer,
        hasGtmStart,
        hasGtmJs,
        dataLayerLength: win.dataLayer?.length || 0
      });
    }

    return isHealthy;
  }

  trackEvent(eventName: string, eventParams?: any): void {

    if (this.gtmDisabled) {
      console.log('GTM disabled');
      return;
    }

    const now = Date.now();
    if (eventName === this.lastTrackedEvent && (now - this.lastEventTime) < 500) {
      console.warn('Duplicate event tracking prevented:', eventName);
      return;
    }

    this.lastTrackedEvent = eventName;
    this.lastEventTime = now;

    const win = this.windowRef.nativeWindow as CustomWindow;
    console.info('GTM TRACKING EVENT: ', eventName, win.dataLayer?.length);

    try {
      let category = '';
      let action = '';
      let label = '';

      if (eventName && (eventName.includes('::') || eventName.includes(':'))) {
        let normalizedName = eventName;
        normalizedName = normalizedName.replace(/:((?!:))/g, '::$1');

        const parts = normalizedName.split('::');

        if (parts.length >= 1) category = parts[0];
        if (parts.length >= 2) action = parts[1];
        if (parts.length >= 3) {
          label = parts.slice(2).join('::');

          if (label.startsWith(':')) {
            label = label.substring(1);
          }
        }

        console.log('Parsed event:', {category, action, label});
      }

      const gtmEvent = {
        event: 'uaevent',
        eventCategory: category,
        eventAction: action,
        eventLabel: label
      };

      console.info('GTM TRACKING EVENT parsed:', gtmEvent);
      this.pushEvent(gtmEvent);
    } catch (error) {
      console.error('Error tracking event:', error);
    }
  }

  formatEventWithParams(eventTemplate: string, params: Record<string, string>): string {
    try {
      let formattedEvent = eventTemplate;

      Object.entries(params).forEach(([key, value]) => {
        const safeValue = value ? String(value).toLowerCase().trim().replace(/\s+/g, '-') : '';
        formattedEvent = formattedEvent.replace(`{${key}}`, safeValue);
      });

      return formattedEvent;
    } catch (error) {
      console.error('Error formatting event:', error);
      return eventTemplate;
    }
  }

  setCustomTrackingForNextNavigation(params: any): void {
    this.customTrackingParams = params;
  }


  setupRouterTracking(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      setTimeout(() => {
        if (this.customTrackingParams) {
          this.trackPageView(event.urlAfterRedirects, this.customTrackingParams);
          this.customTrackingParams = null;
        } else {
          this.trackPageView(event.urlAfterRedirects);
        }
      }, 1000);
    });
  }

  trackPageView(pagePath: string, options: {
    pageTitle?: string,
    [key: string]: any
  } = {}): void {

    if (this.gtmDisabled) {
      console.log('GTM disabled');
      return;
    }

    if (this.isProcessing) {
      console.warn('Preventing recursive trackPageView call');
      return;
    }

    try {
      const pageViewEvent = {
        event: 'updatevirtualpath',
        brand: 'fsp',
        language: this.configService.getLanguage(),
        country: this.configService.getCountry(),
        virtualPageURL: '/fsp/' + this.getPageNameFromUrl(pagePath),
        pageName: 'fsp:' + this.configService.getCountry() + ':' + this.getPageNameFromUrl(pagePath),
        siteTypeLevel1: 'fleet-portal',
        siteTypeLevel2: this.getPageNameFromUrl(pagePath),
        siteOwner: 'central',
        siteTarget: 'B2B',
        siteFamily: 'connected services',
        pageCategory: this.getPageNameFromUrl(pagePath) + " Page",

        ...options
      };

      console.log('Tracking minimal page view:', pageViewEvent);
      this.pushEvent(pageViewEvent);
    } catch (error) {
      console.error('Error tracking page view:', error);
    }
  }

  getPageNameFromUrl(url: string): string {
    try {
      if (url === '/' || url === '' || url.match(/^\/[a-z]{2}-[A-Z]{2}\/?$/)) {
        return 'landing';
      }

      const pathOnly = url.split('?')[0];

      const pathParts = pathOnly.split('/').filter(part => part.length > 0);

      if (pathParts.length === 1 && pathParts[0].match(/^[a-z]{2}-[A-Z]{2}$/)) {
        return 'landing';
      }

      const startIndex = pathParts[0].match(/^[a-z]{2}-[A-Z]{2}$/) ? 1 : 0;

      if (startIndex >= pathParts.length) {
        return 'landing';
      }

      const pageName = pathParts[pathParts.length - 1];

      return pageName;
    } catch (error) {
      console.error('Error extracting page name from URL:', error);
      return 'unknown';
    }
  }

  private setupAutoTracking(): void {
    let clickTimeout: any;

    document.addEventListener('click', (event) => {
      if (clickTimeout) {
        clearTimeout(clickTimeout);
      }

      clickTimeout = setTimeout(() => {
        let target = event.target as HTMLElement;
        let dataEvent: string | null = null;
        let iterations = 0;

        while (target && !dataEvent && iterations < 10) {
          dataEvent = target.getAttribute('data-event');
          if (!dataEvent && target.parentElement) {
            target = target.parentElement;
          } else {
            break;
          }
          iterations++;
        }

        if (dataEvent) {
          this.trackEvent(dataEvent);
        }
      }, 100);
    }, true);
  }

  formatEventName(pageName: string, area: string, label: string, additionalParams?: string[]): string {
    let eventName = `${pageName}::${area}::${label}`;
    if (additionalParams && additionalParams.length > 0) {
      eventName += '::' + additionalParams.join('::');
    }
    return eventName;
  }

  trackEcommerceEvent(data: EcommerceEvents): void {

    if (this.gtmDisabled) {
      console.log('GTM disabled');
      return;
    }

    const event = {
      eventCategory: 'Ecommerce',
      eventAction: data.event,
      currency: data.currency || 'USD',
      ...data
    };

    console.log('Tracking ecommerce event:', event);
    this.pushEvent(event);
  }

  manualTrackPageView(pageName: string): void {
    try {
      const event = {
        event: 'page_view',
        page_title: pageName,
        page_location: window.location.href
      };

      this.queueEvent(event);
    } catch (error) {
      console.error('Manual page tracking error:', error);
    }
  }

  enableSafeRouterTracking(): void {
    let lastUrl = '';
    let trackingTimeout: any;

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {

      if (event.urlAfterRedirects === lastUrl) {
        return;
      }

      lastUrl = event.urlAfterRedirects;

      if (trackingTimeout) {
        clearTimeout(trackingTimeout);
      }

      trackingTimeout = setTimeout(() => {
        try {
          this.manualTrackPageView(this.getPageNameFromUrl(event.urlAfterRedirects));
        } catch (error) {
          console.error('Safe router tracking error:', error);
        }
      }, 1000);
    });
  }
}
